import { useEffect, useState } from "react";
import { ProductCards } from "../ProductCards/ProductCards";
import Button from "../Button/Button";
import { useTranslation } from "react-i18next";
import { useAmplitude } from "../../context/AmplitudeContext";
import { useUID } from "../../context/UIDContext";
import Pagination from "../Pagination/Pagination";
import { ProductsPerPage } from "../../constants";
import { BiFilterAlt } from "react-icons/bi";
import "./styles.css";
import { IoIosArrowDown } from "react-icons/io";
import { Dropdown } from "../DropDown/DropDown";

export const ProductRecommendation = ({
  results,
  recommendedProducts,
  onContinue,
}) => {
  const { t } = useTranslation(["aiResults", "recommendation"]);
  const trackEvent = useAmplitude();
  const { uid } = useUID();

  const [products, setProducts] = useState(recommendedProducts);
  const productCategories =
    recommendedProducts.length > 1
      ? ["Alle", ...new Set(recommendedProducts.map((p) => p.category))]
      : [];

  const [currentPage, setCurrentPage] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState("Alle");
  const [filteredProducts, setFilteredProducts] = useState(recommendedProducts);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  const getPaginatedProducts = () => {
    if (filteredProducts) {
      const startIndex = (currentPage - 1) * ProductsPerPage;
      const endIndex = startIndex + ProductsPerPage;
      const paginated = filteredProducts.slice(startIndex, endIndex);
      return paginated;
    }
    return [];
  };

  const paginatedProducts = getPaginatedProducts();

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    // Track user visits Results Page
    trackEvent("webapp_user_visited_products_page", uid);
  }, [uid, trackEvent]);

  const handleCategoryChange = (newSelectedCategory, products) => {
    if (products) {
      const newData = products.filter(
        (product) =>
          newSelectedCategory === "Alle" ||
          product.category === newSelectedCategory,
      );
      setCurrentPage(1);
      setFilteredProducts(newData);
    }
    setCategoryFilter(newSelectedCategory);
  };

  const onProductSelect = (productId) => {
    setFilteredProducts((prevData) => {
      const updatedData = prevData.map((product) =>
        product.id === productId
          ? { ...product, selected: !product.selected }
          : product,
      );

      return updatedData;
    });
    setProducts((prevData) => {
      const updatedData = prevData.map((product) =>
        product.id === productId
          ? { ...product, selected: !product.selected }
          : product,
      );

      return updatedData;
    });
  };

  return (
    <div className="recommendation-scroll-content">
      {/* Show Results Summary */}
      <div className="product-page_header">
        <BiFilterAlt size={18} className="product-filter-icon" />
        {Object.entries(results).map(([key, value]) => (
          <div key={key} className="product-page-ai-result-wrapper">
            <p>{t(`${key}`)}:</p>
            <p style={{ fontWeight: "500" }}>{t(`${value?.value}`)}</p>
          </div>
        ))}
      </div>
      <div className="product-page-category-section">
        <h4 className="products-page-category-header">
          {t("recommendation:category-title")}
        </h4>
        <h4 className="products-page-category-title">
          {t("recommendation:category")}
        </h4>
        <div
          className="product-page-category-selection-button"
          onClick={() => setShowCategoryDropdown((prevValue) => !prevValue)}
        >
          <p className="product-page-selected-category-text">
            {t(categoryFilter)}
          </p>
          <div
            className={`product-page-category-dropdown-arrow ${showCategoryDropdown ? "up" : ""}`}
          >
            <IoIosArrowDown />
          </div>
          {showCategoryDropdown && (
            <Dropdown
              selectedOption={categoryFilter}
              options={productCategories}
              onSelect={(newSelection) =>
                handleCategoryChange(newSelection, products)
              }
            />
          )}
        </div>
      </div>

      <>
        {/* Products */}
        <div className={`products-wrapper_grid`}>
          {paginatedProducts.length > 0 ? (
            <ProductCards
              layout="grid"
              products={paginatedProducts}
              onProductSelect={onProductSelect}
            />
          ) : (
            <div className="not-found-container">
              <h4 className="not-found-header">
                {t("recommendation:not-found-header")}
              </h4>
              <p className="not-found-text">
                {t("recommendation:not-found-text")}
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredProducts !== null &&
          filteredProducts.length > ProductsPerPage && (
            <Pagination
              currentPage={currentPage}
              totalPages={
                ProductsPerPage > 0
                  ? Math.ceil(filteredProducts.length / ProductsPerPage)
                  : 1
              }
              onPageChange={handlePageChange}
            />
          )}
      </>
      {products !== null && products.length > 0 && (
        <div className="cart-button-wrapper">
          <Button
            text={t("recommendation:to-cart")}
            onClick={() => {
              trackEvent("webapp_user_clicked_cart", uid);
              onContinue(products);
            }}
            disabled={!products.some((product) => product.selected)}
          />
        </div>
      )}
    </div>
  );
};
